// Type for the sections object
type SectionsObject = {
  [key: string]: PolicyFormSection;
};

export const sharedCareScoutSections: SectionsObject = {
  coverageSelection: {
    id: 'coverage-selection',
    title: 'Coverage Selection',
    subsections: [
      {
        id: 'coverage-selection-questions',
        title: 'Coverage Selection',
        questionGroups: [
          {
            id: 'info-group',
            format: 'grid',
            questions: ['applicantName', 'applicantDob'],
          },
          {
            id: 'coverage-maximum-group',
            format: 'default',
            questions: [
              'coverageMaximum',
              'dailyBenefitAmount',
              'deductiblePeriod',
              'waiverOfHomeAndCommunityCare',
              'nonforfeitureBenefit',
              'inflationProtection',
            ],
          },
          {
            id: 'couple-status-group',
            format: 'default',
            questions: ['coupleStatus'],
          },
          {
            id: 'couple-status-related-group',
            format: 'default',
            questions: ['spouseName', 'spouseSsn', 'spouseDob', 'spouseCoverageNumber'],
            conditional: {
              visible: {
                field: 'coupleStatus',
                operator: 'equals',
                value: 'yes',
              },
            },
          },
        ],
      },
      {
        id: 'coverage-selection-premium-info',
        title: 'Premium Information',
        questionGroups: [
          {
            id: 'premium-info-group',
            format: 'default',
            questions: ['premiumPaymentMode'],
          },
          {
            id: 'premium-agent-group',
            format: 'default',
            questions: [
              'agentName',
              'haveAgentProducerCode',
              'agentProducerCode',
              'nationalProducerCode',
              'applicationSignedState',
            ],
          },
        ],
      },
    ],
  },

  // Page 3 (Electronic Funds Transfer (EFT) Payment Authorization for Long Term Care Insurance)
  eftPaymentAuth: {
    id: 'eft-payment-auth',
    title: 'EFT Payment Authorization',
    subsections: [
      {
        id: 'eft-payment-auth-questions',
        title: 'Electronic Funds Transfer (EFT) Payment Authorization for Long Term Care Insurance',
        questionGroups: [
          {
            id: 'info-group',
            format: 'default',
            questions: ['applicantNameEft', 'accountOwnerDifferent'],
          },
          {
            id: 'account-owner-related-group',
            format: 'grid',
            questions: [
              'accountOwnerName',
              'accountOwnerEmail',
              'accountOwnerAddress',
              'accountOwnerCity',
              'accountOwnerState',
              'accountOwnerZipcode',
            ],
            conditional: {
              visible: {
                field: 'accountOwnerDifferent',
                operator: 'equals',
                value: 'yes',
              },
            },
          },
          {
            id: 'account-routing-number',
            format: 'grid',
            questions: ['routingNumber', 'accountNumber'],
          },
        ],
      },
    ],
  },

  // Page 5 - 13 (Application for CareScout Care Assurance Individual Long-Term Care Insurance)
  /* Section A - Applicant Profile */
  applicantProfile: {
    id: 'applicant-profile',
    title: 'Applicant Profile',
    subsections: [
      {
        id: 'personal-information',
        title: 'Personal Information',
        questionGroups: [
          {
            id: 'name-group',
            format: 'grid',
            questions: ['firstName', 'middleInitial', 'lastName', 'applicantPrintName'],
          },
          {
            id: 'status-identification',
            format: 'grid',
            questions: ['maritalStatus', 'socialSecurityNumber'],
          },
          {
            id: 'drivers-license',
            format: 'grid',
            questions: ['driversLicenseNumber', 'driversLicenseState'],
          },
          {
            id: 'contact-info',
            format: 'default',
            questions: ['emailAddress'],
          },
          {
            id: 'contact-info-phone',
            format: 'grid',
            align: 'center',
            questions: ['phoneNumber', 'phoneNumberIsCell'],
          },
          {
            id: 'birth-info',
            format: 'grid',
            questions: ['dateOfBirth', 'birthplace', 'gender'],
          },
          {
            id: 'residence-address',
            format: 'default',
            questions: ['residenceStreetAddress'],
          },
          {
            id: 'residence-location',
            format: 'grid',
            questions: ['residenceCity', 'residenceState', 'residenceZipCode'],
          },
          {
            id: 'mailing-different',
            format: 'default',
            questions: ['differentAddress'],
          },
          {
            id: 'mailing-address',
            format: 'default',
            questions: ['mailingAddress'],
          },
          {
            id: 'mailing-location',
            format: 'grid',
            questions: ['mailingCity', 'mailingState', 'mailingZipCode'],
          },
        ],
      },
    ],
  },

  /* Section B - Insurability Profile */
  insurabilityProfile: {
    id: 'insurability-profile',
    title: 'Insurability Profile',
    subsections: [
      {
        id: 'insurability-questions',
        title: 'Insurability Questions',
        questionGroups: [
          {
            id: 'insurability-screening',
            format: 'default',
            questions: [
              'socialSecurityDisability',
              'assistiveDevices',
              'adlAssistance',
              'homeCareUse',
              'ltcFacility',
              'seriousConditions',
              'seriousCancers',
            ],
          },
        ],
      },
    ],
  },

  /* Section C - Medical Questions */
  medicalQuestions: {
    id: 'medical-questions',
    title: 'Medical Questions',
    subsections: [
      {
        id: 'primary-care-provider',
        title: 'Primary Care Provider',
        questionGroups: [
          {
            id: 'provider-info',
            format: 'default',
            questions: ['primaryCareProviderName'],
          },
          {
            id: 'provider-details',
            format: 'default',
            questions: ['primaryCareProviderAddress', 'primaryCareProviderPhone'],
          },
          {
            id: 'provider-visit',
            format: 'grid',
            questions: ['primaryCareProviderDateLastSeen', 'primaryCareProviderReasonLastSeen'],
          },
        ],
      },
      {
        id: 'physical-measurements',
        title: 'Physical Measurements',
        questionGroups: [
          {
            id: 'height-weight',
            format: 'grid',
            questions: ['heightFeet', 'heightInches', 'weight'],
          },
          {
            id: 'weight-change',
            format: 'default',
            questions: ['weightChange'],
          },
          {
            id: 'weight-change-details',
            format: 'grid',
            questions: ['weightChangeType', 'weightChangePounds', 'weightChangeRegimen'],
          },
        ],
      },
      {
        id: 'medical-conditions-cardiovascular',
        title: 'Medical Questions',
        questionGroups: [
          {
            id: 'heart-conditions-1',
            title: ' ',
            description:
              'Have you ever been diagnosed or advised by a member of the medical profession as having, or in the last 5 years been treated for any of the following conditions:',
            format: 'default',
            questions: [
              'anginaChestPains',
              'anginaChestPainsDetails',
              'heartAttack',
              'heartAttackDetails',
              'heartDisease',
              'heartDiseaseDetails',
              'heartMurmur',
              'heartMurmurDetails',
              'highBloodPressure',
              'highBloodPressureDetails',
              'irregularHeartbeat',
              'irregularHeartbeatDetails',
              'peripheralVascular',
              'peripheralVascularDetails',
            ],
          },
          {
            id: 'digestive-conditions-1',
            format: 'default',
            questions: [
              'colitisCrohns',
              'colitisCrohnsDetails',
              'esophagusDisorder',
              'esophagusDisorderDetails',
              'hepatitis',
              'hepatitisDetails',
              'intestinalDisorder',
              'intestinalDisorderDetails',
              'liverDisorder',
              'liverDisorderDetails',
              'pancreatitis',
              'pancreatitisDetails',
              'stomachDisorder',
              'stomachDisorderDetails',
              'weightLossProcedure',
              'weightLossProcedureDetails',
            ],
          },
          {
            id: 'metabolic-conditions',
            format: 'default',
            questions: [
              'diabetes',
              'diabetesDetails',
              'cancer',
              'cancerDetails',
              'leukemiaLymphoma',
              'leukemiaLymphomaDetails',
              'tumorMassLump',
              'tumorMassLumpDetails',
            ],
          },
          {
            id: 'respiratory-conditions',
            format: 'default',
            questions: [
              'asthma',
              'asthmaDetails',
              'chronicLungDisorder',
              'chronicLungDisorderDetails',
              'sleepApnea',
              'sleepApneaDetails',
            ],
          },
          {
            id: 'blood-vision-conditions',
            format: 'default',
            questions: [
              'bloodDisorder',
              'bloodDisorderDetails',
              'macularDegeneration',
              'macularDegenerationDetails',
            ],
          },
          {
            id: 'mental-conditions',
            format: 'default',
            questions: [
              'bipolarDisorder',
              'bipolarDisorderDetails',
              'depression',
              'depressionDetails',
              'otherMentalDisorder',
              'otherMentalDisorderDetails',
            ],
          },
          {
            id: 'urological-conditions',
            format: 'default',
            questions: [
              'bladderDisorder',
              'bladderDisorderDetails',
              'kidneyDisorder',
              'kidneyDisorderDetails',
              'prostateDisorder',
              'prostateDisorderDetails',
            ],
          },
          {
            id: 'musculoskeletal-conditions-1',
            format: 'default',
            questions: [
              'amputation',
              'amputationDetails',
              'arthritis',
              'arthritisDetails',
              'backSpineNeck',
              'backSpineNeckDetails',
              'boneJointMuscle',
              'boneJointMuscleDetails',
              'osteoporosis',
              'osteoporosisDetails',
            ],
          },
          {
            id: 'neurological-conditions-1',
            format: 'default',
            questions: [
              'memoryLoss',
              'memoryLossDetails',
              'brainNervousSystem',
              'brainNervousSystemDetails',
              'injuryFalls',
              'injuryFallsDetails',
              'paralysis',
              'paralysisDetails',
              'tremors',
              'tremorsDetails',
              'postPolioSyndrome',
              'postPolioSyndromeDetails',
              'seizuresConvulsions',
              'seizuresConvulsionsDetails',
              'strokeTia',
              'strokeTiaDetails',
            ],
          },
          {
            id: 'other-conditions',
            format: 'default',
            questions: [
              'hivAids',
              'hivAidsDetails',
              'lupusScleroderma',
              'lupusSclerodermaDetails',
              'organTransplant',
              'organTransplantDetails',
              'otherCripplingConditions',
              'otherCripplingConditionsDetails',
            ],
          },
          {
            id: 'medications',
            format: 'default',
            questions: [
              'additionalPrescriptionMedications',
              'medicationDetails',
              'advisedByMedicalProfession',
              'advisedByMedicalProfessionDetails',
            ],
          },
          {
            id: 'past-medical-care',
            format: 'default',
            questions: [
              'hospitalPatient',
              'hospitalPatientDetails',
              'otherTreatment',
              'otherTreatmentDetails',
              'incompleteTests',
              'incompleteTestsDetails',
              'assistanceRequired',
              'assistanceRequiredDetails',
              'receivedHomeCare',
              'receivedHomeCareDetails',
            ],
          },
          {
            id: 'disability-info',
            format: 'default',
            questions: [
              'disabilityBenefits',
              'disabilityDescription',
              'disabilityFromDate',
              'disabilityToDate',
            ],
          },
        ],
      },
    ],
  },

  /* Section D - Social History */
  socialHistory: {
    id: 'social-history',
    title: 'Social History',
    subsections: [
      {
        id: 'tobacco-use',
        title: 'Tobacco Use',
        questionGroups: [
          {
            id: 'tobacco-history',
            format: 'default',
            questions: ['tobaccoUse'],
          },
          {
            id: 'tobacco-details',
            format: 'grid',
            questions: ['tobaccoType', 'tobaccoFrequency', 'tobaccoDateLastUsed'],
          },
        ],
      },
      {
        id: 'alcohol-use',
        title: 'Alcohol Use',
        questionGroups: [
          {
            id: 'alcohol-current',
            format: 'default',
            questions: ['drinkAlcohol', 'alcoholEverUsed'],
          },
          {
            id: 'alcohol-history',
            format: 'grid',
            questions: ['alcoholDateLastUsed', 'alcoholReasonQuit'],
          },
          {
            id: 'alcohol-details',
            format: 'grid',
            questions: ['alcoholType', 'alcoholFrequencyAmount'],
          },
          {
            id: 'alcohol-medical',
            format: 'default',
            questions: ['alcoholAdvised', 'alcoholAdvisedDetails'],
          },
          {
            id: 'alcohol-counseling',
            format: 'default',
            questions: ['alcoholCounseling', 'alcoholCounselingDetails'],
          },
        ],
      },
      {
        id: 'drug-use',
        title: 'Drug Use',
        questionGroups: [
          {
            id: 'drug-history',
            format: 'default',
            questions: ['drugUse'],
          },
          {
            id: 'drug-details',
            format: 'grid',
            questions: ['drugSubstanceName', 'drugDateLastUsed', 'drugFrequency'],
          },
          {
            id: 'treatment-history',
            format: 'default',
            questions: ['drugAlcoholTreatment', 'drugAlcoholTreatmentDetails'],
          },
        ],
      },
      {
        id: 'lifestyle',
        title: 'Lifestyle',
        questionGroups: [
          {
            id: 'employment',
            format: 'default',
            questions: ['currentlyEmployed', 'occupation'],
          },
          {
            id: 'activities',
            format: 'default',
            questions: ['hobbiesActivities'],
          },
          {
            id: 'activity-details',
            format: 'grid',
            questions: ['activityTypes', 'activityHoursPerWeek'],
          },
          {
            id: 'activity-details-second',
            format: 'grid',
            questions: ['activityTypesSecond', 'activityHoursPerWeekSecond'],
          },
          {
            id: 'driving',
            format: 'default',
            questions: ['driveAutomobile', 'annualMileage'],
          },
          {
            id: 'living-situation',
            format: 'default',
            questions: ['retirementCommunity', 'retirementServices'],
          },
        ],
      },
    ],
  },

  /* Section E - Family History */
  familyHistory: {
    id: 'family-history',
    title: 'Family History',
    subsections: [
      {
        id: 'father-history',
        title: "Father's Medical History",
        questionGroups: [
          {
            id: 'father-status',
            format: 'grid',
            questions: ['fatherLiving', 'fatherAge', 'fatherAgeAtDeath'],
          },
          {
            id: 'father-conditions',
            format: 'grid',
            questions: ['fatherAlzheimers', 'fatherHuntingtons'],
          },
        ],
      },
      {
        id: 'mother-history',
        title: "Mother's Medical History",
        questionGroups: [
          {
            id: 'mother-status',
            format: 'grid',
            questions: ['motherLiving', 'motherAge', 'motherAgeAtDeath'],
          },
          {
            id: 'mother-conditions',
            format: 'grid',
            questions: ['motherAlzheimers', 'motherHuntingtons'],
          },
        ],
      },
    ],
  },

  // ========DEPRACATED FOR NOW==============
  // /* Section F - Details */
  // details: {
  //   id: 'details',
  //   title: 'Details',
  //   subsections: [
  //     {
  //       id: 'additional-details',
  //       title: 'Additional Details for Yes Responses',
  //       questionGroups: [
  //         {
  //           id: 'details-text',
  //           format: 'default',
  //           questions: ['detailsForYesResponses'],
  //         },
  //       ],
  //     },
  //   ],
  // },

  /* Section G - Other Coverage and Replacement */
  otherCoverage: {
    id: 'other-coverage',
    title: 'Other Coverage and Replacement',
    subsections: [
      {
        id: 'medicaid-enrollment',
        title: 'Other Coverage and Replacement',
        questionGroups: [
          {
            id: 'medicaid-status',
            format: 'default',
            questions: ['currentlyEnrolledMedicaid'],
          },
          {
            id: 'current-ltc',
            format: 'default',
            questions: ['existingLtcInsurance'],
          },
          {
            id: 'current-ltc-details',
            format: 'grid',
            questions: ['existingLtcCompany', 'existingLtcCoverage'],
          },
          {
            id: 'current-ltc-benefits',
            format: 'grid',
            questions: ['existingDailyBenefit', 'existingMonthlyBenefit'],
          },
          {
            id: 'previous-ltc',
            format: 'default',
            questions: ['previousLtcInsurance'],
          },
          {
            id: 'previous-ltc-details',
            format: 'grid',
            questions: ['previousLtcCompany', 'previousLtcLapseDate'],
          },
          {
            id: 'replacement-intent',
            format: 'default',
            questions: ['intendToReplace'],
          },
          {
            id: 'replacement-details',
            format: 'grid',
            questions: ['replaceLtcCoverage', 'replaceCompany', 'replacePolicyNumber'],
          },
          {
            id: 'previous-applications',
            format: 'default',
            questions: ['previousApplicationDeclined'],
          },
          {
            id: 'declined-details',
            format: 'grid',
            questions: ['declinedCompany', 'declinedProduct', 'declinedAction'],
          },
          {
            id: 'declined-reason',
            format: 'grid',
            questions: ['declinedReason', 'declinedDateOfAction'],
          },
        ],
      },
    ],
  },

  /* Section H - Protection Against Unintentional Lapse */
  protectionLapse: {
    id: 'protection-lapse',
    title: 'Protection Against Unintentional Lapse',
    subsections: [
      {
        id: 'lapse-notification',
        title: 'Lapse Notification Designee',
        questionGroups: [
          {
            id: 'lapse-election',
            format: 'default',
            questions: ['protectionAgainstUnintentionalLapse'],
          },
          {
            id: 'designee-info',
            format: 'default',
            questions: [
              'protectionAgainstUnintentionalLapseName',
              'protectionAgainstUnintentionalLapseStreetAddress',
            ],
          },
          {
            id: 'designee-location',
            format: 'grid',
            questions: [
              'protectionAgainstUnintentionalLapseCity',
              'protectionAgainstUnintentionalLapseState',
              'protectionAgainstUnintentionalLapseZipCode',
            ],
          },
          {
            id: 'designee-contact',
            format: 'grid',
            questions: [
              'protectionAgainstUnintentionalLapsePhoneNumber',
              'protectionAgainstUnintentionalLapseRelationship',
            ],
          },
        ],
      },
    ],
  },

  /* Section J - Producer Signature */
  producerSignature: {
    id: 'producer-signature',
    title: 'To be completed by the Producer',
    subsections: [
      {
        id: 'signature',
        title: 'To be completed by the Producer',
        questionGroups: [
          {
            id: 'producer-policies-sold',
            format: 'default',
            questions: ['producerPoliciesSoldToApplicant', 'producerPoliciesSoldToApplicant5Years'],
          },
        ],
      },
    ],
  },

  // Page 14 (Long-Term Care Insurance Application Overflow Form)
  ltcAppOverflowForm: {
    id: 'ltc-app-overflow-form',
    title: 'LTC Insurance Application Overflow Form',
    subsections: [
      {
        id: 'ltc-app-overflow-form-questions',
        title: 'Long-Term Care Insurance Application Overflow Form',
        questionGroups: [
          {
            id: 'ltc-app-overflow-remarks',
            format: 'default',
            questions: ['applicationOverflowRemarksAutoCollected', 'applicationOverflowRemarks'],
          },
        ],
      },
    ],
  },

  // Page 15 (Health Insurance Authorization)
  healthInsuranceAuthorization: {
    id: 'health-insurance-authorization',
    title: 'Health Insurance Authorization',
    subsections: [
      {
        id: 'health-insurance-authorization-questions',
        title: 'Health Insurance Authorization',
        questionGroups: [
          {
            id: 'health-insurance-authorization-ssn',
            format: 'default',
            questions: ['last4DigitsOfSSN'],
          },
        ],
      },
    ],
  },

  // Page 16 (Long-Term Care Insurance Personal Worksheet)
  ltcInsurancePersonalWorksheet: {
    id: 'ltc-insurance-personal-worksheet',
    title: 'Long-Term Care Insurance Personal Worksheet',
    subsections: [
      {
        id: 'ltc-worksheet-premium-questions',
        title: 'Section A: Premium Information',
        questionGroups: [
          {
            id: 'ltc-worksheet-premium',
            format: 'grid',
            questions: [
              'personalWorksheetPremiumMonthly',
              'personalWorksheetPremiumQuarterly',
              'personalWorksheetPremiumSemiAnnually',
              'personalWorksheetPremiumTotal',
            ],
          },
        ],
      },
      {
        id: 'ltc-worksheet-financial-disclosure',
        title: 'Financial Information Disclosure',
        questionGroups: [
          {
            id: 'ltc-worksheet-financial-disclosure',
            format: 'default',
            questions: [
              'personalWorksheetPreDisclosure',
            ],
          },
        ],
      },
      {
        id: 'ltc-worksheet-income-questions',
        title: 'Section B: Questions About Your Income',
        questionGroups: [
          {
            id: 'ltc-worksheet-income',
            format: 'default',
            questions: [
              'personalWorksheetPayResources',
              'personalWorksheetPayResourcesOther',
              'personalWorksheetSourceOfFunds',
              'personalWorksheetAffordPolicyIfSpouseDies',
              'personalWorksheetPremiumIncreaseResponse',
              'personalWorksheetHouseholdIncome',
              'personalWorksheetIncomeChange',
              'personalWorksheetIncomeAbilityImpact',
              'personalWorksheetBuyInflationProtection',
              'personalWorksheetFutureCostCoverage',
              'personalWorksheetFutureCostCoverageOther',
              'personalWorksheetDeductiblePeriod',
              'personalWorksheetDeductiblePeriodPayment',
            ],
          },
        ],
        conditional: {
          visible: {
            field: 'personalWorksheetPreDisclosure',
            operator: 'equals' as const,
            value: 'yes',
          }
        },
      },
      {
        id: 'ltc-worksheet-savings-questions',
        title: 'Section C: Questions About Your Savings and Investments',
        questionGroups: [
          {
            id: 'ltc-worksheet-savings',
            format: 'default',
            questions: ['personalWorksheetAssetsWorth', 'personalWorksheetAssetsChange'],
          },
        ],
        conditional: {
          visible: {
            field: 'personalWorksheetPreDisclosure',
            operator: 'equals' as const,
            value: 'yes',
          }
        },
      },
      {
        id: 'ltc-worksheet-disclosure',
        title: 'Disclosure Statement',
        questionGroups: [
          {
            id: 'ltc-worksheet-disclosure',
            title: 'Disclosure Statement',
            format: 'default',
            questions: [
              'personalWorksheetDisclosureConfirmation',
              'personalWorksheetDisclosureAgreement',
              'personalWorksheetNotSuitable',
            ],
          },
        ],
      },
    ],
  },

  // Page 19 (Verification of Financial Non-Disclosure)
  verificationFinancialNonDisclosure: {
    id: 'verification-financial-non-disclosure',
    title: 'Verification of Financial Non-Disclosure',
    subsections: [
      {
        id: 'verification-financial-non-disclosure-questions',
        title: 'Verification of Financial Non-Disclosure',
        questionGroups: [
          {
            id: 'non-disclosure-verification',
            title: ' ',
            description:
              'An approved policy WILL NOT BE ISSUED until the Long Term Care Insurance Personal Worksheet (and if applicable, the Verification of Financial Non-Disclosure) has been fully completed and received by the company.',
            format: 'default',
            questions: ['financialNonDisclosureDecision'],
          },
        ],
        conditional: {
          visible: {
            field: 'personalWorksheetPreDisclosure',
            operator: 'equals' as const,
            value: 'no',
          }
        }
      },
    ],
  },

  // Page 20 (Long-Term Insurance Potential Rate Increase Disclosure Form)
  ltcPotentialRateIncreaseDisclosure: {
    id: 'ltc-potential-rate-increase-disclosure',
    title: 'Long-Term Insurance Potential Rate Increase Disclosure Form',
    subsections: [
      {
        id: 'ltc-potential-rate-increase-disclosure-questions',
        title: 'Long-Term Insurance Potential Rate Increase Disclosure',
        questionGroups: [
          {
            id: 'ltc-rate-increase-premium-amount',
            format: 'grid',
            questions: ['premiumAmount'],
          },
        ],
      },
    ],
  },

  // Page 22 (Licensed Insurance Producer Information)
  licensedInsuranceProducerInformation: {
    id: 'licensed-insurance-producer-information',
    title: 'Licensed Insurance Producer Information',
    subsections: [
      {
        id: 'licensed-insurance-producer-questions',
        title: 'Licensed Insurance Producer Information',
        questionGroups: [
          {
            id: 'li-producer-policy-info',
            format: 'default',
            questions: ['producerInfoPolicyNumber'],
          },
          {
            id: 'li-producer-primary-contact',
            format: 'grid',
            title: "Primary Licensed Insurance Producer's Contact Information",
            questions: [
              'producerInfoName',
              'producerInfoStreetAddress',
              'producerInfoCity',
              'producerInfoState',
              'producerInfoZipcode',
              'producerInfoLicenseOrTin',
              'producerInfoPhone',
              'producerInfoEmail',
            ],
          },
          {
            id: 'li-producer-primary-contact-related',
            format: 'default',
            questions: ['producerInfoBrokerageAgency', 'producerInfoProducerCode'],
          },
          {
            id: 'li-producer-commission-split',
            format: 'default',
            title: 'Commission Split Producer Information',
            questions: ['producerInfoCommissionSplit'],
          },
          {
            id: 'li-producer-name-and-address',
            format: 'default',
            questions: ['nameAndAddressOfProducer'],
          },
        ],
      },
    ],
  },
};
